"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Github, Linkedin, Mail, ArrowUp } from "lucide-react";
import Logo from "@/public/logo.png";
import Image from "next/image";
import { usePortfolio } from "@/components/portfolio-provider";

export default function Footer() {
  const { data } = usePortfolio();
  const config = data?.siteConfig;
  const heroData = data?.hero;

  const scrollToTop = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (typeof window !== "undefined" && window.location.hash) {
      window.history.replaceState(null, "", "/");
    }
    const lenis =
      typeof window !== "undefined" ? (window as any).__lenis : null;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(0, { immediate: false, duration: 0.9 });
    } else if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <footer className="relative border-t border-white/10 bg-[#0e0b07]/95 pt-14 pb-5 overflow-hidden">
      {/* Subtle Top Specular Rim */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-between gap-8 md:flex-row">
          {/* Logo & Bio */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-3.5 md:items-start"
          >
            <Link
              href="/"
              onClick={scrollToTop}
              className="flex items-center gap-2 transition-transform active:scale-95"
            >
              <Image
                src={config?.logoUrl || Logo}
                alt="Logo"
                width={144}
                height={36}
                className="max-sm:w-28 w-36 object-contain"
              />
            </Link>
            <p className="text-sm text-muted-foreground text-center md:text-left max-w-md">
              {config?.footerBio ||
                "Full Stack Software Engineer building high-performance web products with React, Next.js, Node.js, and Nest.js."}
            </p>
          </motion.div>

          {/* Socials & Back to Top */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col items-center gap-4 sm:flex-row"
          >
            <div className="flex items-center gap-3">
              <a
                href={heroData?.githubUrl || "https://github.com/sakib-xrz"}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${heroData?.fullName || "Md Sakibul Islam"} GitHub profile`}
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)] transition-all duration-300 hover:border-primary/50 hover:bg-primary/15 hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] active:scale-95"
              >
                <Github className="h-4 w-4 text-foreground/85 transition-transform duration-300 group-hover:scale-110 group-hover:text-foreground" />
              </a>
              <a
                href={
                  heroData?.linkedinUrl || "https://linkedin.com/in/sakib0808"
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${heroData?.fullName || "Md Sakibul Islam"} LinkedIn profile`}
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)] transition-all duration-300 hover:border-primary/50 hover:bg-primary/15 hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] active:scale-95"
              >
                <Linkedin className="h-4 w-4 text-foreground/85 transition-transform duration-300 group-hover:scale-110 group-hover:text-foreground" />
              </a>
              <a
                href={`mailto:${heroData?.email || "sakibul.islam0808@gmail.com"}`}
                aria-label={`Send email to ${heroData?.fullName || "Md Sakibul Islam"}`}
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)] transition-all duration-300 hover:border-primary/50 hover:bg-primary/15 hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] active:scale-95"
              >
                <Mail className="h-4 w-4 text-foreground/85 transition-transform duration-300 group-hover:scale-110 group-hover:text-foreground" />
              </a>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={scrollToTop}
              className="liquid-ghost-pill rounded-full h-10 px-5 text-xs font-semibold text-foreground flex items-center gap-1.5"
            >
              <ArrowUp className="h-3.5 w-3.5 text-primary" />
              <span>Back to Top</span>
            </Button>
          </motion.div>
        </div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 border-t border-white/10 pt-6 text-center text-xs text-muted-foreground"
        >
          <p className="text-center">
            {config?.copyrightText ||
              `©${new Date().getFullYear()} Md Sakibul Islam. All rights reserved.`}
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
